/**
 * The one way this SPA talks to the API (SPEC §10).
 *
 * Every response passes through {@link request}, so the §10 error envelope is
 * parsed in exactly one place and every caller gets an {@link ApiError} with a
 * machine-readable `code` and, for a 422, the per-field messages the ParamBar
 * renders inline (§9.4).
 *
 * Nothing here retries automatically or falls back to a default on failure. A
 * report view that quietly shows the previous result after a failed refetch is
 * the browser-side version of the silent wrongness the API refuses to produce.
 */

import type {
  DepartmentSummary,
  Me,
  Option,
  OptionsResponse,
  ReportDefinition,
  ReportResult,
  ReportSummary,
  SnapshotRefresh,
} from './types'

/** A structured failure from the API, or a transport failure shaped like one. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  /** Per-field messages from a 422 (§10). */
  readonly fields: Readonly<Record<string, string>>
  /** Present on a 401: where to send the user to log in (§5.2). */
  readonly loginUrl: string | null

  constructor(
    status: number,
    code: string,
    message: string,
    fields: Record<string, string> = {},
    loginUrl: string | null = null,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.fields = fields
    this.loginUrl = loginUrl
  }

  /** A 404 on a report path means "no such report, or not yours" (§7.2). */
  get isNotFound(): boolean {
    return this.status === 404
  }

  get isUnauthenticated(): boolean {
    return this.status === 401
  }

  /** Worth offering a retry button for; a 4xx is not (§9.4). */
  get isRetryable(): boolean {
    return this.status >= 500 || this.status === 0
  }
}

/** The CSRF token from `/api/me`, replayed on state-changing requests (§5.2). */
let csrfToken: string | null = null

export function setCsrfToken(token: string): void {
  csrfToken = token
}

interface Envelope {
  error?: { code?: string; message?: string; fields?: Record<string, string>; login_url?: string }
}

interface LogoutResult {
  logout_url?: string | null
}

/**
 * Exported so `lib/admin.ts` can build on the same envelope handling rather
 * than growing a second fetch path. There is one place that parses a §10
 * error, and this is it.
 */
export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Built through `Headers` rather than by object spread: `HeadersInit` may be
  // an array of pairs, and spreading one into an object yields numeric keys.
  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  // A FormData body (a file upload) sets its own multipart boundary; naming a
  // Content-Type here would replace it and the server could not parse the parts.
  if (init.body !== undefined && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }
  // Sent on every mutating request. `run` and `options` are exempt server-side,
  // but sending it costs nothing and keeps this to one code path.
  if (csrfToken !== null && init.method !== undefined && init.method !== 'GET') {
    headers.set('X-CSRF-Token', csrfToken)
  }

  let response: Response
  try {
    response = await fetch(path, { ...init, headers })
  } catch (cause) {
    // The network, not the API. Status 0 marks it as retryable.
    throw new ApiError(0, 'network_error', cause instanceof Error ? cause.message : String(cause))
  }

  if (response.status === 204) return undefined as T

  const body: unknown = await response.json().catch(() => null)

  if (!response.ok) {
    const envelope = (body ?? {}) as Envelope
    const error = envelope.error ?? {}
    throw new ApiError(
      response.status,
      error.code ?? 'error',
      error.message ?? `${path} returned ${String(response.status)}`,
      error.fields ?? {},
      error.login_url ?? null,
    )
  }

  if (body === null) {
    throw new ApiError(response.status, 'malformed_response', `${path} returned no JSON body`)
  }
  return body as T
}

export const api = {
  me: (): Promise<Me> => request<Me>('/api/me'),

  /**
   * Ends the session (§5.2).
   *
   * Deliberately a fetch and not a plain `<form method="post">`: the endpoint
   * is behind `require_csrf`, and a form cannot send the `X-CSRF-Token`
   * header — the button 403'd for that reason until it was found by signing
   * out of the running stack at M4.
   */
  logout: async (): Promise<LogoutResult | undefined> => {
    return await request<LogoutResult | undefined>('/auth/logout', { method: 'POST' })
  },

  departments: (): Promise<DepartmentSummary[]> =>
    request<DepartmentSummary[]>('/api/departments'),

  departmentReports: (key: string): Promise<ReportSummary[]> =>
    request<ReportSummary[]>(`/api/departments/${encodeURIComponent(key)}/reports`),

  report: (slug: string): Promise<ReportDefinition> =>
    request<ReportDefinition>(`/api/reports/${encodeURIComponent(slug)}`),

  /** POST because the parameter payload is too structured for a query string. */
  runReport: (slug: string, params: Record<string, unknown>): Promise<ReportResult> =>
    request<ReportResult>(`/api/reports/${encodeURIComponent(slug)}/run`, {
      method: 'POST',
      body: JSON.stringify({ params }),
    }),

  /**
   * Reload a snapshot report's dataset (§8.9, §9.4).
   *
   * Not a cache purge: it re-reads the source, which is what a reader asking
   * "is this today's numbers?" actually wants, and it needs no admin rights.
   * Takes seconds against a real view, so the caller shows progress.
   */
  refreshReport: (slug: string): Promise<SnapshotRefresh> =>
    request<SnapshotRefresh>(`/api/reports/${encodeURIComponent(slug)}/refresh`, {
      method: 'POST',
    }),

  options: (
    slug: string,
    param: string,
    dependencies: Record<string, unknown>,
  ): Promise<readonly Option[]> =>
    request<OptionsResponse>(
      `/api/reports/${encodeURIComponent(slug)}/options/${encodeURIComponent(param)}`,
      { method: 'POST', body: JSON.stringify({ params: dependencies }) },
    ).then((response) => response.options),
}
